import { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useApolloClient } from '@apollo/client'
import ROUTES from '../utils/routes'
import { courseFindMany } from '../../graphql/Courses/courseFindMany'
import { CourseType, ModuleType, LessonType } from '../../types'
import { useLesson } from '.'
import { getCourseLessons } from '../CourseLibraryView/utils/getCourseLessons'
import { getFormattedCourse } from '../CourseLibraryView/utils/getFormattedCourse'
import useSegment from '../utils/useSegment'

// This function basically allows us to handle all the course, module, and
// lesson information, as well as all the completion logic on the frontend,
// without having to poll or wait to update the lesson view.

export const useCourseHandler = () => {
  const apollo = useApolloClient()
  const history = useHistory()
  const { track } = useSegment()
  const { courseSlug, lessonSlug }: any = useParams()
  const {
    getNextLesson, handleComplete, handleSkip, handleUnSkip
  } = useLesson()

  const [loading, setLoading] = useState(true)
  const [nextLesson, setNextLesson] = useState<LessonType>()

  const [lessons, setLessons] = useState<LessonType[]>([])
  const [modules, setModules] = useState<ModuleType[]>([])

  const [currCourse, setCurrCourse] = useState<CourseType>()
  const [currModule, setCurrModule] = useState<ModuleType>()
  const [currLesson, setCurrLesson] = useState<LessonType>()

  // Loads the current course and all its values
  const loadCourse = async () => {
    const { data } = await apollo.query({
      fetchPolicy: 'no-cache',
      query: courseFindMany,
      variables: { slug: courseSlug }
    })

    const course = data.courseFindMany.docs[0] as CourseType

    if (!course) {
      return history.push(ROUTES.dashboard.courses)
    }

    // Get and format the course's lessons
    const lessons = getCourseLessons(course)
    const formattedLessons = lessons.map((lesson) => {
      return {
        ...lesson,
        isCompleted: lesson.userLesson?.isCompleted,
        isSkipped: lesson.userLesson?.isSkipped
      }
    })

    const formattedCourse = getFormattedCourse(course, course.modules || [], formattedLessons)

    setLessons(formattedLessons)
    setModules(course.modules || [])
    setCurrCourse(formattedCourse)
    setLoading(false)
  }

  // Finds the current lesson and sets currLesson
  const findCurrentLesson = async () => {
    if (!currCourse || !currCourse.modules) {
      return
    }

    const currentLesson = lessons.find((lesson) => lesson.slug === lessonSlug)

    if (!currentLesson) {
      return
    }

    await checkForSkippedLessons(currentLesson)
    await handleUnSkip(currentLesson)

    const nextLesson = await getNextLesson(currentLesson, currCourse)

    setCurrLesson(currentLesson)
    findCurrentModule(currentLesson)
    setNextLesson(nextLesson)
    setLoading(false)
  }

  // Finds the current module and sets currModule
  const findCurrentModule = (currLesson?: LessonType) => {
    if (!modules.length || !currLesson) {
      return
    }

    const currentModule = modules.find((module) =>
      module.lessons?.find((lesson) => lesson.module === module._id))
    setCurrModule(currentModule)
  }

  // Toggles the designated lesson complete or uncomplete
  const toggleLessonComplete = async (lesson: LessonType, moveToNext?: boolean) => {
    if (!currCourse || !currLesson) {
      return
    }

    const isCurrLesson = lesson._id === currLesson?._id

    if (isCurrLesson) {
      setCurrLesson({
        ...currLesson,
        isCompleted: !currLesson.isCompleted
      })
    }

    if (isCurrLesson && !currLesson.isCompleted && nextLesson && moveToNext) {
      history.push(ROUTES.courses.lessonBySlug(currCourse.slug, nextLesson.slug))
    }

    const lessonModule = modules.find((module) => module._id === lesson.module)
    const updatedLessons = lessons
    const lessonIndex = lessons.indexOf(lesson)
    updatedLessons[lessonIndex].isCompleted = !lesson.isCompleted

    const formattedCourse = getFormattedCourse(currCourse, modules, updatedLessons)
    setCurrCourse(formattedCourse)
    await handleComplete(lesson)
    await checkForSkippedLessons(lesson)

    // Track the completed lesson
    track('Completed Lesson', {
      id: lesson._id,
      title: lesson.title,
      course_title: currCourse.title,
      course_id: currCourse._id,
      module_title: currModule?.title,
      module_id: currModule?._id
    })

    // Track when all lessons in the module have been completed
    const uncompletedModuleLessons = updatedLessons.filter(
      (lesson) => lesson.module === lessonModule?._id && !lesson.isCompleted
    )
    if (!uncompletedModuleLessons.length) {
      track('Completed Module', {
        id: lessonModule?._id,
        title: lessonModule?.title,
        course_title: currCourse.title,
        course_id: currCourse._id
      })
    }

    // Track when all lessons in the course have been completed
    if (!updatedLessons.filter((lesson) => !lesson.isCompleted).length) {
      track('Completed Course', {
        id: currCourse._id,
        title: currCourse.title
      })
    }
  }

  // Identifies and skips any applicable lessons before the designated lesson
  const checkForSkippedLessons = async (lesson: LessonType) => {
    if (!currCourse?.modules || !currLesson) {
      return
    }

    const updatedLessons = lessons
    const lessonIndex = updatedLessons.indexOf(lesson)
    const lessonsToSkip = updatedLessons.slice(0, lessonIndex)

    lessonsToSkip.forEach(async (lessonToSkip, index) => {
      if (lessonToSkip._id === currLesson._id) {
        setCurrLesson({
          ...currLesson,
          isSkipped: true
        })
      }

      await handleSkip(lessonToSkip)
      updatedLessons[index].isSkipped = true
    })

    setLessons(updatedLessons)
  }

  useEffect(() => {
    if (currCourse && lessonSlug) {
      setLoading(true)
      findCurrentLesson()
    }
  }, [currCourse, lessonSlug])

  useEffect(() => {
    setLoading(true)
    loadCourse()
  }, [])

  return {
    lessons,
    modules,
    currCourse,
    currModule,
    currLesson,
    nextLesson,
    loading,
    actions: { toggleLessonComplete }
  }
}
